/**
 * @fileoverview BIP39 Utility Functions
 *
 * Wraps the @scure/bip39 library to provide secure BIP39 mnemonic generation,
 * validation, entropy conversion, and seed derivation for cryptographic key
 * management workflows.
 *
 * All operations use the standard English BIP39 wordlist. The library uses the
 * Web Crypto API (globalThis.crypto) internally, ensuring CSPRNG-based entropy.
 *
 * **Security notes:**
 * - Never log mnemonic phrases or seeds — they are equivalent to private keys.
 * - Use the logger only for non-sensitive diagnostic messages.
 * - `mnemonicToSeed` is irreversible (PBKDF2-based KDF); entropy round-tripping
 *   must use `mnemonicToEntropy` / `entropyToMnemonic` instead.
 *
 * @module utils/bip39Utils
 * @requires @scure/bip39
 * @requires @xanectech/lib
 */

import {
  generateMnemonic as scureGenerateMnemonic,
  validateMnemonic as scureValidateMnemonic,
  mnemonicToEntropy as scureMnemonicToEntropy,
  entropyToMnemonic as scureEntropyToMnemonic,
  mnemonicToSeedWebcrypto,
} from '@scure/bip39';
import { wordlist as englishWordlist } from '@scure/bip39/wordlists/english.js';
import { createLogger } from '@xanectech/lib';

const logger = createLogger('bip39Utils');

/**
 * The number of bits of entropy used when generating a 24-word mnemonic.
 * BIP39 requires 256 bits of entropy to produce 24 words.
 */
const MNEMONIC_STRENGTH_256 = 256;

// ============================================================================
// Wordlist Access
// ============================================================================

/**
 * Return the English BIP39 wordlist (2048 words).
 *
 * @returns A readonly array of the 2048 English BIP39 words.
 */
export function getWordlist(): readonly string[] {
  return englishWordlist;
}

// ============================================================================
// Mnemonic Generation
// ============================================================================

/**
 * Generate a cryptographically secure BIP39 mnemonic phrase.
 *
 * Uses the Web Crypto API (CSPRNG) for entropy generation and the standard
 * English BIP39 wordlist.
 *
 * @param strength - Entropy strength in bits. Must be a multiple of 32 in the
 *   range 128–256. Defaults to 256 (24 words). Common values:
 *   - 128 → 12 words
 *   - 160 → 15 words
 *   - 192 → 18 words
 *   - 224 → 21 words
 *   - 256 → 24 words
 * @returns A space-separated BIP39 mnemonic string.
 */
export function generateMnemonic(
  strength: number = MNEMONIC_STRENGTH_256,
): string {
  const mnemonic = scureGenerateMnemonic(englishWordlist, strength);
  return mnemonic;
}

// ============================================================================
// Validation
// ============================================================================

/**
 * Validate a BIP39 mnemonic phrase against the English wordlist.
 *
 * Checks that:
 * - All words are present in the English BIP39 wordlist.
 * - The word count is valid (12, 15, 18, 21, or 24 words).
 * - The embedded checksum is correct.
 *
 * @param mnemonic - The mnemonic phrase to validate.
 * @returns `true` if the mnemonic is valid, `false` otherwise.
 */
export function validateMnemonic(mnemonic: string): boolean {
  const valid = scureValidateMnemonic(mnemonic, englishWordlist);
  return valid;
}

// ============================================================================
// Entropy Conversion
// ============================================================================

/**
 * Convert a BIP39 mnemonic phrase back to its raw entropy bytes.
 *
 * This is the inverse of `entropyToMnemonic`. The returned entropy can be used
 * to deterministically reconstruct the mnemonic at a later point.
 *
 * @param mnemonic - A valid BIP39 mnemonic phrase (must pass `validateMnemonic`).
 * @returns The raw entropy bytes encoded in the mnemonic.
 * @throws If the mnemonic is invalid.
 */
export function mnemonicToEntropy(mnemonic: string): Uint8Array {
  return scureMnemonicToEntropy(mnemonic, englishWordlist);
}

/**
 * Convert raw entropy bytes to a BIP39 mnemonic phrase.
 *
 * This is the inverse of `mnemonicToEntropy`. Entropy length must correspond
 * to a valid BIP39 word count:
 * - 16 bytes (128 bits) → 12 words
 * - 20 bytes (160 bits) → 15 words
 * - 24 bytes (192 bits) → 18 words
 * - 28 bytes (224 bits) → 21 words
 * - 32 bytes (256 bits) → 24 words
 *
 * @param entropy - Raw entropy bytes.
 * @returns A BIP39 mnemonic phrase.
 * @throws If the entropy length is not valid.
 */
export function entropyToMnemonic(entropy: Uint8Array): string {
  return scureEntropyToMnemonic(entropy, englishWordlist);
}

// ============================================================================
// Seed Derivation
// ============================================================================

/**
 * Derive a 64-byte seed from a BIP39 mnemonic phrase using PBKDF2.
 *
 * Uses the Web Crypto API implementation (`mnemonicToSeedWebcrypto`) for
 * browser compatibility. This operation is **irreversible** — you cannot
 * recover the mnemonic from the seed.
 *
 * @param mnemonic - A valid BIP39 mnemonic phrase.
 * @param passphrase - An optional passphrase that further protects the seed.
 *   Defaults to an empty string (standard BIP39 behavior).
 * @returns A 64-byte seed derived from the mnemonic and passphrase.
 */
export async function mnemonicToSeed(
  mnemonic: string,
  passphrase?: string,
): Promise<Uint8Array> {
  const seed = await mnemonicToSeedWebcrypto(mnemonic, passphrase);
  return seed;
}
